import { LocaleKey, LOCALE_CONFIG } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";

interface SchemaOrgProps {
  schema: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Legacy JSON-LD injector. After the TanStack Start cutover, all route JSON-LD
 * lives in the native `head()` of each route file. This component is kept as a
 * no-op so existing call sites still compile; the exported schema builders
 * below remain the source of truth for those route `head()` scripts.
 */
export const SchemaOrg = (_props: SchemaOrgProps) => null;

// Locale-specific content for schemas
interface LocalizedContent {
  alternateName: string;
  description: string;
  slogan: string;
  websiteDescription: string;
  inLanguage: string;
}

const SCHEMA_TRANSLATIONS: Record<LocaleKey, LocalizedContent> = {
  es: {
    alternateName: "Fora da Rota - Viajes a Brasil",
    description: "Agencia de viajes especializada en Brasil. Hoteles, tours y experiencias únicas en los mejores destinos brasileños.",
    slogan: "Tu aventura brasileña comienza aquí",
    websiteDescription: "Reserva hoteles, actividades y experiencias únicas en Brasil",
    inLanguage: "es",
  },
  en: {
    alternateName: "Fora da Rota - Travel to Brazil",
    description: "Travel agency specialized in Brazil. Hotels, tours and unique experiences in the best Brazilian destinations.",
    slogan: "Your Brazilian adventure starts here",
    websiteDescription: "Book hotels, activities and unique experiences in Brazil",
    inLanguage: "en",
  },
  'en-EU': {
    alternateName: "Fora da Rota - Travel to Brazil",
    description: "Travel agency specialized in Brazil. Hotels, tours and unique experiences in the best Brazilian destinations.",
    slogan: "Your Brazilian adventure starts here",
    websiteDescription: "Book hotels, activities and unique experiences in Brazil",
    inLanguage: "en-GB",
  },
  'pt-BR': {
    alternateName: "Fora da Rota - Viagens ao Brasil",
    description: "Agência de viagens especializada no Brasil. Hotéis, passeios e experiências únicas nos melhores destinos brasileiros.",
    slogan: "Sua aventura brasileira começa aqui",
    websiteDescription: "Reserve hotéis, atividades e experiências únicas no Brasil",
    inLanguage: "pt-BR",
  },
};

// Get currency config for schema
const getCurrencyConfig = (locale: LocaleKey) => {
  const config = LOCALE_CONFIG[locale];
  return {
    currency: config.currency,
    symbol: config.currencySymbol,
  };
};

// Canonical base URL for @id consistency (used before helper functions are defined)
const ORG_ID = 'https://foradarota.com/#organization';

// Organization schema for the company (multilingual) - Online business without physical address
export const getOrganizationSchema = (locale: LocaleKey = 'es') => {
  const content = SCHEMA_TRANSLATIONS[locale];
  const baseUrl = getLocalizedUrl(locale, '/');

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    "name": "Fora da Rota",
    "alternateName": content.alternateName,
    "url": baseUrl,
    "logo": {
      "@type": "ImageObject",
      "@id": "https://foradarota.com/#logo",
      "url": "https://foradarota.com/logo.png",
      "contentUrl": "https://foradarota.com/logo.png",
      "caption": "Fora da Rota Logo"
    },
    "image": "https://foradarota.com/og-image.jpg",
    "description": content.description,
    "slogan": content.slogan,
    "foundingDate": "2024",
    "areaServed": [
      { "@type": "Country", "name": "Brasil" },
      { "@type": "GeoShape", "name": "Worldwide" },
    ],

    "contactPoint": {
      "@type": "ContactPoint",
      "@id": "https://foradarota.com/#contact",
      "contactType": "customer service",
      "availableLanguage": ["Spanish", "Portuguese", "English"],
      "email": "contacto@foradarota.com",
      "areaServed": "Worldwide",
    },

    // Explicitly signal online operation
    "location": {
      "@type": "VirtualLocation",
      "url": baseUrl,
    },

    "sameAs": [
      "https://www.instagram.com/foradarota.com.br/",
      "https://www.facebook.com/people/Fora-da-Rota/61586050490567/",
      "https://share.google/H62SZeHrmPRCLZ5Ei",
      "https://br.trustpilot.com/review/foradarota.com",
      "https://www.reclameaqui.com.br/empresa/gj-empreendimentos-ltda/"
    ],
  };
};

// OnlineBusiness schema - for businesses that operate entirely online without physical location
export const getOnlineBusinessSchema = (locale: LocaleKey = 'es') => {
  const content = SCHEMA_TRANSLATIONS[locale];
  const baseUrl = getLocalizedUrl(locale, '/');

  return {
    "@context": "https://schema.org",
    "@type": "OnlineBusiness",
    "@id": "https://foradarota.com/#onlinebusiness",
    "name": "Fora da Rota",
    "alternateName": content.alternateName,
    "description": content.description,
    "url": baseUrl,
    "logo": {
      "@id": "https://foradarota.com/#logo"
    },
    "image": "https://foradarota.com/og-image.jpg",
    "email": "contacto@foradarota.com",

    // Link to Organization entity using consistent @id
    "parentOrganization": {
      "@id": ORG_ID
    },

    // No physical address - online only
    "areaServed": {
      "@type": "GeoShape",
      "name": "Worldwide",
    },

    // 24/7 online availability
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      "opens": "00:00",
      "closes": "23:59",
    },

    "priceRange": "€€-€€€",
    "currenciesAccepted": "EUR, USD, BRL",
    "paymentAccepted": "Credit Card, Debit Card, PIX",
    "serviceType": ["Hotel Booking", "Online Travel Agency", "Tourism Services"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Travel Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Hotel Reservations",
            "serviceType": "Online Booking",
          },
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Resort Packages",
            "serviceType": "Online Booking",
          },
        },
      ],
    },

    "sameAs": [
      "https://www.instagram.com/foradarota.com.br/",
      "https://www.facebook.com/people/Fora-da-Rota/61586050490567/",
      "https://share.google/H62SZeHrmPRCLZ5Ei",
      "https://br.trustpilot.com/review/foradarota.com",
      "https://www.reclameaqui.com.br/empresa/gj-empreendimentos-ltda/"
    ],
    "knowsLanguage": ["es", "en", "pt-BR"],
    "availableLanguage": ["Spanish", "Portuguese", "English"],
  };
};

// Deprecated: keeping for backward compatibility but should use getOnlineBusinessSchema instead
export const getLocalBusinessSchema = getOnlineBusinessSchema;

// Canonical base URL for consistent @id references
const CANONICAL_BASE_URL = 'https://foradarota.com';

// Helper to get consistent Organization @id
export const getOrganizationId = () => `${CANONICAL_BASE_URL}/#organization`;

// Helper to get consistent WebSite @id  
export const getWebSiteId = () => `${CANONICAL_BASE_URL}/#website`;

// WebSite schema for search functionality (multilingual)
export const getWebSiteSchema = (locale: LocaleKey = 'es') => {
  const content = SCHEMA_TRANSLATIONS[locale];
  const baseUrl = getLocalizedUrl(locale, '/');
  const searchUrl = getLocalizedUrl(locale, '/hoteles');
  
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": getWebSiteId(),
    "url": baseUrl,
    "name": "Fora da Rota",
    "description": content.websiteDescription,
    "inLanguage": content.inLanguage,
    // Reference to the publishing organization
    "publisher": {
      "@id": getOrganizationId()
    },
    "potentialAction": {
      "@type": "SearchAction",
      "@id": `${CANONICAL_BASE_URL}/#search-action`,
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${searchUrl}?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "availableLanguage": [
      {
        "@type": "Language",
        "name": "Spanish",
        "alternateName": "es"
      },
      {
        "@type": "Language",
        "name": "English",
        "alternateName": "en"
      },
      {
        "@type": "Language",
        "name": "Portuguese",
        "alternateName": "pt-BR"
      }
    ]
  };
};

// Breadcrumb schema with consistent @id and url
export const getBreadcrumbSchema = (
  items: { name: string; url?: string }[],
  pageUrl?: string
) => {
  // Use the last item's URL or provided pageUrl for the @id
  const lastItemWithUrl = [...items].reverse().find(item => item.url);
  const baseId = pageUrl || lastItemWithUrl?.url || '';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    ...(baseId && { "@id": `${baseId}#breadcrumb` }),
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url && { 
        "item": {
          "@type": "WebPage",
          "@id": item.url,
          "url": item.url,
          "name": item.name
        }
      })
    }))
  };
};

// Hotel schema for search results (ItemList) - with currency support
export const getHotelListSchema = (
  hotels: Array<{
    name: string;
    url: string;
    image?: string;
    starRating?: number;
    pricePerNight?: number;
    address?: string;
  }>,
  locale: LocaleKey = 'es'
) => {
  const { currency, symbol } = getCurrencyConfig(locale);
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": hotels.map((hotel, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Hotel",
        "name": hotel.name,
        "url": hotel.url,
        ...(hotel.image && { "image": hotel.image }),
        ...(hotel.starRating && { 
          "starRating": {
            "@type": "Rating",
            "ratingValue": hotel.starRating
          }
        }),
        ...(hotel.pricePerNight && {
          "priceRange": `${symbol}${Math.floor(hotel.pricePerNight)} - ${symbol}${Math.ceil(hotel.pricePerNight * 1.5)}`,
          "offers": {
            "@type": "Offer",
            "priceCurrency": currency,
            "price": hotel.pricePerNight.toFixed(2)
          }
        }),
        ...(hotel.address && {
          "address": {
            "@type": "PostalAddress",
            "streetAddress": hotel.address,
            "addressCountry": "BR"
          }
        })
      }
    })),
    "numberOfItems": hotels.length
  };
};

// Schema mode: 'google' for Rich Results optimization, 'full' for complete Schema.org
export type SchemaMode = 'google' | 'full';

// Hotel detail input interface
interface HotelDetailInput {
  name: string;
  description?: string;
  url: string;
  images?: string[];
  starRating?: number;
  address?: string;
  city?: string;
  postalCode?: string;
  addressLocality?: string;
  addressRegion?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  checkInTime?: string;
  checkOutTime?: string;
  checkInDate?: string;
  checkOutDate?: string;
  rooms?: Array<{
    name: string;
    description?: string;
    pricePerNight: number;
    totalPrice: number;
    bedType?: string;
    mealPlan?: string;
    occupancy?: { adults: number; children?: number };
  }>;
  amenities?: string[];
  petsAllowed?: boolean;
  review?: {
    text: string;
    author?: string;
  };
  hotelId?: string | number;
}

// Detailed Hotel schema for hotel detail page - with currency support
// mode: 'google' = optimized for Google Rich Results, 'full' = complete Schema.org
export const getHotelDetailSchema = (
  hotel: HotelDetailInput,
  locale: LocaleKey = 'es',
  mode: SchemaMode = 'full'
) => {
  const { currency } = getCurrencyConfig(locale);
  
  // Build address object with all available fields
  const buildAddress = () => {
    const addressObj: Record<string, string> = {
      "@type": "PostalAddress",
      "addressCountry": "BR"
    };
    
    if (hotel.address) {
      addressObj.streetAddress = hotel.address;
    }
    if (hotel.addressLocality || hotel.city) {
      addressObj.addressLocality = hotel.addressLocality || hotel.city || '';
    }
    if (hotel.addressRegion) {
      addressObj.addressRegion = hotel.addressRegion;
    }
    if (hotel.postalCode) {
      addressObj.postalCode = hotel.postalCode;
    }
    
    return addressObj;
  };
  
  // Generate a unique identifier for the hotel
  const hotelId = `${hotel.url}#hotel`;
  
  // Filter images to ensure they are valid URLs
  const validImages = hotel.images?.filter(img => img && img.startsWith('http')) || [];
  
  // Format check-in/check-out times in ISO 8601 format (HH:MM:SS or HH:MM)
  const formatTime = (time?: string): string => {
    if (!time) return "14:00:00";
    // If already has seconds, return as-is
    if (time.match(/^\d{2}:\d{2}:\d{2}$/)) return time;
    // If just HH:MM, add seconds
    if (time.match(/^\d{2}:\d{2}$/)) return `${time}:00`;
    return "14:00:00";
  };
  
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "@id": hotelId,
    "identifier": hotel.hotelId?.toString() || hotelId,
    "name": hotel.name,
    "url": hotel.url,
    // Description is required for valid Hotel schema
    "description": hotel.description || `${hotel.name} - Hotel en Brasil`,
    // Image must be valid URLs
    ...(validImages.length > 0 && { "image": validImages }),
    ...(hotel.starRating && {
      "starRating": {
        "@type": "Rating",
        "ratingValue": hotel.starRating,
        "bestRating": 5
      }
    }),
    // Address is always included for Hotel schema validation
    "address": buildAddress(),
    ...(hotel.phone && { "telephone": hotel.phone }),
    ...(hotel.latitude && hotel.longitude && {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": hotel.latitude,
        "longitude": hotel.longitude
      }
    }),
    "checkinTime": formatTime(hotel.checkInTime),
    "checkoutTime": formatTime(hotel.checkOutTime || "12:00"),
    "petsAllowed": hotel.petsAllowed ?? false,
    "currenciesAccepted": "EUR, USD, BRL",
    "paymentAccepted": "Credit Card, Debit Card",
    // Reference to the publishing organization
    "isPartOf": {
      "@id": getWebSiteId()
    },
    "provider": {
      "@id": getOrganizationId()
    },
    ...(hotel.amenities && hotel.amenities.length > 0 && {
      "amenityFeature": hotel.amenities.map(amenity => ({
        "@type": "LocationFeatureSpecification",
        "name": amenity,
        "value": true
      }))
    }),
    // Add review from manual info if available
    ...(hotel.review?.text && {
      "review": {
        "@type": "Review",
        "@id": `${hotel.url}#review`,
        "reviewBody": hotel.review.text,
        "author": {
          "@type": "Organization",
          "@id": getOrganizationId(),
          "name": hotel.review.author || "Fora da Rota"
        },
        "publisher": {
          "@id": getOrganizationId()
        }
      }
    })
  };

  // Add room offers using makesOffer (more appropriate for Hotel than containsPlace)
  if (hotel.rooms && hotel.rooms.length > 0) {
    const pricesPerNight = hotel.rooms.map(r => r.pricePerNight);
    const lowPricePerNight = Math.min(...pricesPerNight);
    const highPricePerNight = Math.max(...pricesPerNight);
    
    const totalPrices = hotel.rooms.map(r => r.totalPrice);
    const lowTotalPrice = Math.min(...totalPrices);
    const highTotalPrice = Math.max(...totalPrices);
    
    // Generate unique room slug
    const roomSlug = (roomName: string, idx: number) => 
      `${hotel.hotelId || hotel.name.toLowerCase().replace(/\s+/g, '-')}-room-${idx + 1}`;

    // Use makesOffer for room offers with proper structure
    // Mode 'google' uses simplified schema for better validation
    // Mode 'full' includes additional Schema.org properties
    schema.makesOffer = hotel.rooms.map((room, index) => {
      const offerId = `${hotel.url}#offer-${index + 1}`;
      const roomId = `${hotel.url}#room-${index + 1}`;
      
      const baseOffer: Record<string, unknown> = {
        "@type": "Offer",
        "@id": offerId,
        "url": `${hotel.url}#room-${index + 1}`,
        "identifier": roomSlug(room.name, index),
        "name": room.name,
        "price": Number(room.totalPrice.toFixed(2)),
        "priceCurrency": currency,
        "availability": "https://schema.org/InStock",
        "validFrom": new Date().toISOString().split('T')[0],
        // Use HotelRoom instead of Product for hotel context (Google valid type)
        "itemOffered": {
          "@type": "HotelRoom",
          "@id": roomId,
          "name": room.name,
          ...(room.description && { "description": room.description }),
          ...(room.bedType && {
            "bed": {
              "@type": "BedDetails",
              "typeOfBed": room.bedType
            }
          }),
          ...(room.occupancy && {
            "occupancy": {
              "@type": "QuantitativeValue",
              "value": room.occupancy.adults + (room.occupancy.children || 0)
            }
          })
        },
      };

      // Add extra properties only for 'full' mode
      if (mode === 'full') {
        // Add description to offer
        if (room.description) {
          baseOffer.description = room.description;
        }

        // Add check-in/out times
        if (hotel.checkInDate && hotel.checkInTime) {
          baseOffer.checkinTime = `${hotel.checkInDate}T${formatTime(hotel.checkInTime)}`;
        }
        if (hotel.checkOutDate && hotel.checkOutTime) {
          baseOffer.checkoutTime = `${hotel.checkOutDate}T${formatTime(hotel.checkOutTime || "12:00")}`;
        }

        // Add meal plan as additional property
        if (room.mealPlan) {
          baseOffer.additionalProperty = {
            "@type": "PropertyValue",
            "name": "Meal Plan",
            "value": room.mealPlan
          };
        }
      }

      return baseOffer;
    });

    // Add aggregate offer - Google Rich Results compatible
    // Note: AggregateOffer does NOT support priceSpecification according to Google
    const aggregateOfferId = `${hotel.url}#aggregate-offer`;
    
    schema.offers = {
      "@type": "AggregateOffer",
      "@id": aggregateOfferId,
      "url": hotel.url,
      "lowPrice": lowPricePerNight.toFixed(2),
      "highPrice": highPricePerNight.toFixed(2),
      "priceCurrency": currency,
      "offerCount": hotel.rooms.length,
      "availability": "https://schema.org/InStock",
    };
    
    // Add priceRange in human-readable format for 'full' mode only
    if (mode === 'full') {
      schema.priceRange = `${currency} ${lowPricePerNight.toFixed(0)} - ${highPricePerNight.toFixed(0)} / night`;
    }
  }

  return schema;
};

// FAQ Schema for common questions - enhanced with @id for hotel context
export const getFAQSchema = (
  faqs: Array<{ question: string; answer: string }>,
  hotelUrl?: string
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  ...(hotelUrl && { "@id": `${hotelUrl}#faq` }),
  ...(hotelUrl && { "url": hotelUrl }),
  "mainEntity": faqs.map((faq, index) => ({
    "@type": "Question",
    ...(hotelUrl && { "@id": `${hotelUrl}#faq-q${index + 1}` }),
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      ...(hotelUrl && { "@id": `${hotelUrl}#faq-a${index + 1}` }),
      "text": faq.answer
    }
  }))
});

// Product schema for checkout items (LodgingReservation for hotels) - with currency support
export const getCheckoutProductsSchema = (
  products: Array<{
    name: string;
    description?: string;
    image?: string;
    price: number;
    hotelName?: string;
    checkIn?: string;
    checkOut?: string;
    roomType?: string;
    numGuests?: number;
  }>,
  locale: LocaleKey = 'es'
) => {
  const { currency } = getCurrencyConfig(locale);
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LodgingReservation",
        "reservationFor": {
          "@type": "LodgingBusiness",
          "name": product.hotelName || product.name,
          ...(product.image && { "image": product.image })
        },
        ...(product.checkIn && { "checkinTime": product.checkIn }),
        ...(product.checkOut && { "checkoutTime": product.checkOut }),
        ...(product.roomType && { 
          "reservedTicket": {
            "@type": "Ticket",
            "ticketedSeat": {
              "@type": "Seat",
              "seatingType": product.roomType
            }
          }
        }),
        ...(product.numGuests && {
          "numAdults": product.numGuests
        }),
        "totalPrice": {
          "@type": "PriceSpecification",
          "price": product.price.toFixed(2),
          "priceCurrency": currency
        },
        "priceCurrency": currency,
        "reservationStatus": "https://schema.org/ReservationPending"
      }
    })),
    "numberOfItems": products.length
  };
};

// Product schema for hotel detail page - improves Google price display
// Google Rich Results requires: offers, review, or aggregateRating
// We always include offers as it's the most relevant for hotel booking
export const getHotelProductSchema = (
  hotel: {
    name: string;
    description?: string;
    url: string;
    images?: string[];
    starRating?: number;
    address?: string;
    minPrice: number; // Required - Google needs offers
    maxPrice?: number;
    offerCount?: number;
    checkIn?: string;
    checkOut?: string;
    review?: {
      text: string;
      author?: string;
    };
  },
  locale: LocaleKey = 'es'
) => {
  const { currency } = getCurrencyConfig(locale);
  const baseUrl = getLocalizedUrl(locale, '/');
  
  // Generate unique identifiers
  const productId = `${hotel.url}#product`;
  const offerId = `${hotel.url}#product-offer`;
  
  // Build the base schema
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": productId,
    "name": hotel.name,
    "description": hotel.description || `Reserva de hotel en ${hotel.name}`,
    "url": hotel.url,
    "brand": {
      "@type": "Brand",
      "name": "Fora da Rota"
    },
    "category": "Hotel Accommodation",
    // Reference to the organization
    "isRelatedTo": {
      "@id": getOrganizationId()
    },
    // REQUIRED: offers - Google Rich Results needs this for Product snippets
    "offers": {
      "@type": "AggregateOffer",
      "@id": offerId,
      "url": hotel.url,
      "lowPrice": hotel.minPrice.toFixed(2),
      "highPrice": hotel.maxPrice ? hotel.maxPrice.toFixed(2) : hotel.minPrice.toFixed(2),
      "priceCurrency": currency,
      "offerCount": hotel.offerCount || 1,
      "availability": "https://schema.org/InStock",
      "validFrom": hotel.checkIn || new Date().toISOString().split('T')[0],
      "priceValidUntil": hotel.checkOut || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      ...(hotel.checkOut && { "validThrough": hotel.checkOut }),
      "seller": {
        "@type": "Organization",
        "@id": getOrganizationId(),
        "name": "Fora da Rota",
        "url": baseUrl
      }
    }
  };

  // Add images if available (valid URLs only)
  if (hotel.images && hotel.images.length > 0) {
    const validImages = hotel.images.filter(img => img && img.startsWith('http'));
    if (validImages.length > 0) {
      schema.image = validImages;
    }
  }

  // Add review if available (helps with Google Rich Results)
  if (hotel.review?.text) {
    schema.review = {
      "@type": "Review",
      "@id": `${hotel.url}#product-review`,
      "reviewBody": hotel.review.text,
      "author": {
        "@type": "Organization",
        "@id": getOrganizationId(),
        "name": hotel.review.author || "Fora da Rota"
      },
      "publisher": {
        "@id": getOrganizationId()
      }
    };
  }

  return schema;
};

// Order schema for completed purchases - with currency support
export const getOrderSchema = (
  order: {
    orderNumber: string;
    orderDate?: string;
    totalPrice: number;
    customerEmail?: string;
    customerName?: string;
    items: Array<{
      name: string;
      price: number;
      hotelName?: string;
      checkIn?: string;
      checkOut?: string;
      roomType?: string;
    }>;
    paymentStatus?: 'pending' | 'completed' | 'failed';
  },
  locale: LocaleKey = 'es'
) => {
  const { currency } = getCurrencyConfig(locale);
  const baseUrl = getLocalizedUrl(locale, '/');
  
  return {
    "@context": "https://schema.org",
    "@type": "Order",
    "orderNumber": order.orderNumber,
    "orderDate": order.orderDate || new Date().toISOString(),
    "orderStatus": order.paymentStatus === 'completed' 
      ? "https://schema.org/OrderDelivered"
      : "https://schema.org/OrderProcessing",
    "priceCurrency": currency,
    "price": order.totalPrice.toFixed(2),
    ...(order.customerEmail && {
      "customer": {
        "@type": "Person",
        ...(order.customerName && { "name": order.customerName }),
        "email": order.customerEmail
      }
    }),
    "acceptedOffer": order.items.map((item, index) => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "LodgingReservation",
        "reservationId": `${order.orderNumber}-${index + 1}`,
        "reservationFor": {
          "@type": "LodgingBusiness",
          "name": item.hotelName || item.name
        },
        ...(item.checkIn && { "checkinTime": item.checkIn }),
        ...(item.checkOut && { "checkoutTime": item.checkOut }),
        ...(item.roomType && {
          "reservedTicket": {
            "@type": "Ticket",
            "ticketedSeat": {
              "@type": "Seat",
              "seatingType": item.roomType
            }
          }
        }),
        "reservationStatus": "https://schema.org/ReservationConfirmed"
      },
      "price": item.price.toFixed(2),
      "priceCurrency": currency
    })),
    "merchant": {
      "@type": "Organization",
      "@id": `${baseUrl}#organization`,
      "name": "Fora da Rota",
      "url": baseUrl
    },
    "paymentMethod": "https://schema.org/CreditCard",
    "paymentMethodId": "Stripe"
  };
};

// Resort/All Inclusive service schema
interface ResortServiceSchemaTranslations {
  name: string;
  description: string;
  serviceType: string;
}

const RESORT_SERVICE_TRANSLATIONS: Record<LocaleKey, ResortServiceSchemaTranslations> = {
  es: {
    name: "Reserva de Resorts All Inclusive",
    description: "Servicio de reservas de resorts all inclusive en Brasil, Caribe, África y Europa. Encuentra las mejores ofertas con todo incluido: comidas, bebidas y actividades.",
    serviceType: "Reservas de Resorts All Inclusive"
  },
  en: {
    name: "All Inclusive Resort Booking",
    description: "All inclusive resort booking service in Brazil, Caribbean, Africa and Europe. Find the best deals with everything included: meals, drinks and activities.",
    serviceType: "All Inclusive Resort Booking"
  },
  'en-EU': {
    name: "All Inclusive Resort Booking",
    description: "All inclusive resort booking service in Brazil, Caribbean, Africa and Europe. Find the best deals with everything included: meals, drinks and activities.",
    serviceType: "All Inclusive Resort Booking"
  },
  'pt-BR': {
    name: "Reserva de Resorts All Inclusive",
    description: "Serviço de reservas de resorts all inclusive no Brasil, Caribe, África e Europa. Encontre as melhores ofertas com tudo incluído: refeições, bebidas e atividades.",
    serviceType: "Reservas de Resorts All Inclusive"
  }
};

export const getResortServiceSchema = (locale: LocaleKey = 'es', destinationName?: string) => {
  const content = RESORT_SERVICE_TRANSLATIONS[locale];
  const baseUrl = getLocalizedUrl(locale, '/resorts');
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${baseUrl}#resort-service`,
    "url": baseUrl,
    "logo": "https://foradarota.com/logo.png",
    "image": "https://foradarota.com/og-image.jpg",
    "description": content.description,
    "priceRange": "€€-€€€",
    "currenciesAccepted": "EUR, USD, BRL",
    "paymentAccepted": "Credit Card, Debit Card, PIX",
    "areaServed": destinationName ? {
      "@type": "Place",
      "name": destinationName
    } : [
      { "@type": "Country", "name": "Brasil" },
      { "@type": "Place", "name": "Caribe" },
      { "@type": "Continent", "name": "África" },
      { "@type": "Continent", "name": "Europa" }
    ],
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": content.name,
        "description": content.description,
        "serviceType": content.serviceType,
        "provider": {
          "@type": "Organization",
          "@id": `${getLocalizedUrl(locale, '/')}#organization`,
          "name": "Fora da Rota"
        }
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": content.serviceType,
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "LodgingReservation",
            "name": content.name
          }
        }
      ]
    },
    "knowsLanguage": ["es", "en", "pt-BR"],
    "sameAs": [
      "https://www.instagram.com/foradarota"
    ]
  };
};

export default SchemaOrg;
